import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { toast } from "sonner";

const MAX_POLLS = 12;
const POLL_MS = 2000;

/** Handles the return leg from Stripe checkout: polls payment status until paid/failed/timeout. */
export const usePaymentReturn = (onPaid) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [payState, setPayState] = useState(null); // null | "checking" | "success" | "error"
  const [paidAmount, setPaidAmount] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (searchParams.get("cancelled")) {
      toast.info("Checkout cancelled — no charge was made");
      navigate("/credits", { replace: true });
      return;
    }
    if (!sessionId) return;
    setPayState("checking");
    pollCount.current = 0;
    const timer = setInterval(async () => {
      pollCount.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearInterval(timer);
          setPaidAmount(data.credits_amount);
          setPayState("success");
          onPaid();
          navigate("/credits", { replace: true });
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          clearInterval(timer);
          setPayState("error");
          navigate("/credits", { replace: true });
          return;
        }
      } catch (err) {
        logError("Status poll failed", err);
      }
      if (pollCount.current >= MAX_POLLS) {
        clearInterval(timer);
        setPayState("error");
        navigate("/credits", { replace: true });
      }
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [searchParams, navigate, onPaid]);

  return { payState, paidAmount };
};
